import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Skeleton component for loading states
 */
const Skeleton = React.forwardRef(
    ({ className, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    "animate-pulse rounded-md bg-muted",
                    className
                )}
                {...props}
            />
        )
    }
)
Skeleton.displayName = "Skeleton"

/**
 * Alert component for feedback messages
 */
const alertVariants = cva(
    "relative w-full rounded-lg border p-md",
    {
        variants: {
            variant: {
                default: "bg-background text-foreground",
                destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
                success: "border-primary/50 bg-primary/5 text-primary",
                warning: "border-accent/50 bg-accent/5 text-accent-foreground",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
)

const Alert = React.forwardRef(
    ({ className, variant, ...props }, ref) => (
        <div
            ref={ref}
            role="alert"
            className={cn(alertVariants({ variant }), className)}
            {...props}
        />
    )
)
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef(
    ({ className, ...props }, ref) => (
        <h5
            ref={ref}
            className={cn("mb-1 font-medium leading-none tracking-tight", className)}
            {...props}
        />
    )
)
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("text-sm [&_p]:leading-relaxed", className)}
            {...props}
        />
    )
)
AlertDescription.displayName = "AlertDescription"

/**
 * Progress component for loading/progress indication
 */
const Progress = React.forwardRef(
    ({ className, value, max = 100, ...props }, ref) => {
        const percentage = Math.min(Math.max((value / max) * 100, 0), 100)

        return (
            <div
                ref={ref}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={max}
                aria-valuenow={value}
                className={cn(
                    "relative h-2 w-full overflow-hidden rounded-full bg-secondary",
                    className
                )}
                {...props}
            >
                <div
                    className="h-full w-full flex-1 bg-primary transition-all duration-base"
                    style={{ transform: `translateX(-${100 - percentage}%)` }}
                />
            </div>
        )
    }
)
Progress.displayName = "Progress"

export { Skeleton, Alert, AlertTitle, AlertDescription, Progress }
